import { useCallback, useState } from 'react';
import {
  Stack,
  Text,
  Secondary,
  Inline,
  InlineItem,
  UtilityButton,
} from '@kinesis/bungle';
import { OrganisationServiceAccount } from './organisation-service-account';
import { NewServiceAccount as NewServiceAccountModal } from './new-service-account';
import { AccessKeyConfirmation } from '../access-keys/access-key-confirmation';
import { Layout } from '@/components/layout';
import {
  Me,
  NewServiceAccount,
  ServiceAccount,
  AccessKeySecret,
} from '@/types';
import { noop } from 'lodash/fp';

const OrganisationServiceAccounts = (
  props: OrganisationServiceAccountsProps,
) => {
  const {
    organisationId,
    me,
    accounts,
    onDelete = noop,
    onDeleteKey = noop,
    onCreate = noop,
    isCreating = false,
    newAccessKey,
    onNewAccessKey = noop,
    onManageConnectors = noop,
    onDismissNewAccessKey,
    onClickKey,
    onUpdateDescription,
    now,
  } = props;
  const [showNewServiceAccountModal, setShowNewServiceAccountModal] =
    useState(false);
  const dismissNewServiceAccountModal = useCallback(
    () => setShowNewServiceAccountModal(false),
    [setShowNewServiceAccountModal],
  );
  const openNewServiceAccountModal = useCallback(
    () => setShowNewServiceAccountModal(true),
    [setShowNewServiceAccountModal],
  );
  const isEditor =
    me.memberships.find(
      ({ organisationId: memberOrganisationId }) =>
        memberOrganisationId === organisationId,
    )?.permissions?.editor ?? false;

  return (
    <>
      {newAccessKey && (
        <AccessKeyConfirmation
          onDismissNewKey={onDismissNewAccessKey}
          accessKey={newAccessKey}
        />
      )}
      {showNewServiceAccountModal && (
        <NewServiceAccountModal
          onDismiss={dismissNewServiceAccountModal}
          onNewServiceAccount={onCreate}
        />
      )}
      <Layout variant='narrow'>
        <Stack space='medium'>
          <Text>
            Service accounts provide an identity and a security context for
            other applications when they integrate with Kinesis.
          </Text>
          {accounts.length === 0 ? (
            <Stack space='small'>
              <Text>
                <Secondary>
                  Create a service account to give an external application or
                  integration its own identity and access keys, separate from
                  any user account.
                </Secondary>
              </Text>
              <Inline>
                <InlineItem>
                  <UtilityButton
                    icon='plus'
                    onClick={openNewServiceAccountModal}
                    disabled={isCreating || !isEditor}
                    magnitude='small'
                    variant='accent'
                  >
                    Create your first service account
                  </UtilityButton>
                </InlineItem>
              </Inline>
            </Stack>
          ) : (
            <>
              <Stack space='none'>
                {accounts.map((account) => (
                  <OrganisationServiceAccount
                    key={account.id}
                    now={now}
                    isEditor={isEditor}
                    account={account}
                    onDelete={onDelete}
                    onDeleteKey={onDeleteKey}
                    onNewAccessKey={onNewAccessKey}
                    onManageConnectors={onManageConnectors}
                    onClickKey={onClickKey}
                    onUpdateDescription={onUpdateDescription}
                  />
                ))}
              </Stack>
              <Inline>
                <InlineItem>
                  <UtilityButton
                    icon='plus'
                    onClick={openNewServiceAccountModal}
                    disabled={isCreating || !isEditor}
                    magnitude='small'
                  >
                    New service account
                  </UtilityButton>
                </InlineItem>
              </Inline>
            </>
          )}
        </Stack>
      </Layout>
    </>
  );
};

export type OrganisationServiceAccountsProps = {
  now: string;
  organisationId: number;
  me: Me;
  accounts: ServiceAccount[];
  onDelete?: (account: ServiceAccount) => void;
  onDeleteKey?: (accountId: number, id: string) => void;
  onCreate?: (account: NewServiceAccount) => void;
  isCreating?: boolean;
  onNewAccessKey?: (account: ServiceAccount) => void;
  onManageConnectors?: (account: ServiceAccount) => void;
  newAccessKey?: AccessKeySecret;
  onDismissNewAccessKey?: () => void;
  onClickKey?: (id: string) => void;
  onUpdateDescription?: (accountId: number, description: string | null) => void;
};

export { OrganisationServiceAccounts };
